import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { type EmployeePlanning } from '../../../types/planning';
import { DayCell } from './DayCell';
import { EmployeeAvatar } from '../../../components/Shared/EmployeeAvatar';
import { UtilizationBar } from '../../../components/Shared/UtilizationBar';
import { weekTotals } from '../../../utils/planningTransformer';
import { isBackOfficeRole } from '../../../utils/roles';

function fmt(hours: number): string {
  const v = Math.round((hours + Number.EPSILON) * 10) / 10;
  return v.toString();
}

interface EmployeeRowProps {
  employee: EmployeePlanning;
  onTaskDragStart?: (taskId: string, employeeId: string, date: string) => void;
  onTaskDragEnd?: () => void;
  onDayDrop?: (employeeId: string, date: string) => void;
  onDueTaskDrop?: (taskId: string, employeeId: string, date: string, durationMinutes: number) => void;
  onTaskClick?: (taskId: string, employeeId: string, date: string) => void;
  onAddTask?: (employeeId: string, date: string) => void;
  onAddAbsence?: (employeeId: string, date: string) => void;
  onAddWorkDay?: (employeeId: string, date: string) => void;
  onEditDay?: (employeeId: string, date: string) => void;
  onRemoveDay?: (employeeId: string, date: string) => void;
  /** Opens the assign-work-schedule-model dialog for employees without a model. */
  onAssignWorkScheduleModel?: (employeeId: string) => void;
}

export function EmployeeRow({
  employee,
  onTaskDragStart,
  onTaskDragEnd,
  onDayDrop,
  onDueTaskDrop,
  onTaskClick,
  onAddTask,
  onAddAbsence,
  onAddWorkDay,
  onEditDay,
  onRemoveDay,
  onAssignWorkScheduleModel,
}: EmployeeRowProps) {
  const { t } = useTranslation('planning');
  const totals = weekTotals(employee);

  return (
    <div className="grid grid-cols-[minmax(140px,180px)_repeat(7,minmax(140px,1fr))] border-b border-gray-200 dark:border-gray-700 min-w-0">
      <div className="bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 p-2 xl:p-3 flex items-center gap-2 min-w-0">
        <EmployeeAvatar
          employee={{ id: employee.id, name: employee.name, avatarColor: employee.avatarColor }}
          size={32}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 min-w-0">
            <div
              className="font-medium text-xs xl:text-sm leading-tight text-gray-900 dark:text-white truncate"
              title={employee.name}
            >
              {employee.name}
            </div>
            {employee.workScheduleModelId === null && (
              <button
                type="button"
                onClick={() => onAssignWorkScheduleModel?.(employee.id)}
                title={t('noWorkScheduleModel')}
                aria-label={t('noWorkScheduleModel')}
                className="flex-shrink-0 text-amber-500 hover:text-amber-600 rounded p-0.5 hover:bg-amber-50"
              >
                <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
            )}
          </div>
          {totals.target > 0 && (
            <>
              <div className="mt-0.5 text-[10px] text-ink-muted dark:text-text-light text-tabular">
                {t('rowSummary.weekHours', { planned: fmt(totals.planned), target: fmt(totals.target) })}
              </div>
              <div className="mt-1">
                <UtilizationBar planned={totals.planned} target={totals.target} height={3} />
              </div>
            </>
          )}
        </div>
      </div>

      {employee.days.map((day) => (
        <DayCell
          key={day.date}
          day={day}
          employeeId={employee.id}
          employeeName={employee.name}
          isBackOffice={isBackOfficeRole(employee.role)}
          onTaskDragStart={onTaskDragStart}
          onTaskDragEnd={onTaskDragEnd}
          onDrop={onDayDrop}
          onDueTaskDrop={onDueTaskDrop}
          onTaskClick={(taskId) => onTaskClick?.(taskId, employee.id, day.date)}
          onAddTask={onAddTask}
          onAddAbsence={onAddAbsence}
          onAddWorkDay={onAddWorkDay}
          onEditDay={onEditDay}
          onRemoveDay={onRemoveDay}
        />
      ))}
    </div>
  );
}
